import { ref } from 'vue';

import { getIoInstance } from '@/socket';

/**
 * Runaway guard only — the server owns when to stop hopping.
 *
 * An instance prefers to serve one signing user at a time, so a Deaf user
 * arriving while the local slot is busy is sent around the load balancer with a
 * running count of refusals. Once that count reaches the server's limit
 * (APP_ASL_PLACEMENT_MAX_HOPS) the next instance takes them anyway, so the
 * normal path always ends in a conversation rather than an error. This cap only
 * catches instances refusing past their own limit, which means something is
 * broken rather than busy.
 */
const runawayHopCap = 20;

/**
 * Gives up on a placement that stops making progress.
 *
 * `runawayHopCap` only counts reassignments, so it cannot catch the case where
 * an instance never answers at all — a dropped emit, or one that dies mid-hop.
 * Without a wall clock the user waits on a reply that is never coming, behind
 * an overlay that never lifts. A normal placement is bounded by
 * APP_ASL_PLACEMENT_MAX_HOPS (8), each hop a socket reconnect through the load
 * balancer: about 6s in AWS, and only ~100ms against localhost, where the hops
 * never leave the machine. Five times the real ceiling, so it fires for an
 * instance that never answers rather than for one that is merely slow.
 */
const placementTimeoutMs = 30_000;

type PlacedEmit = (attempt: number) => void;

let pendingEmit: PlacedEmit | null = null;
let onExhausted: (() => void) | null = null;
let attempts = 0;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Invalidates replays that are still queued on a disconnected socket.
 *
 * A hop reconnects before it re-emits, so the replay sits in SocketInstance's
 * queue until the new connection reports itself connected — several seconds,
 * which is the whole reason Cancel exists. Cancelling or timing out in that gap
 * clears this module but cannot reach that queue, so the emit still fired and
 * the server still seated the user: a failure message followed by a conversation
 * they had backed out of, and a room left behind. Bumping this on every
 * clearPending makes the queued replay a no-op when it finally runs.
 */
let placementEpoch = 0;

/**
 * True while an action is waiting for an instance to accept it.
 *
 * Exposed as a ref rather than pushed into Vuex because placement is socket
 * state, not app state: it starts and ends inside this module, and the only
 * consumer is the layout that renders the waiting overlay. Reactive so that
 * overlay can follow it across the reconnects that move the user between
 * instances.
 */
export const isPlacing = ref(false);

/** Ends the pending placement and stops its clock. */
const clearPending = (): void => {
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  pendingEmit = null;
  onExhausted = null;
  attempts = 0;
  isPlacing.value = false;
  placementEpoch += 1;
};

/**
 * Hands control back to the caller that started the placement, so its own
 * pending flags reset. Callers surface this as "could not reach a station",
 * which also covers a user who stopped waiting.
 */
const giveUp = (): void => {
  const exhausted = onExhausted;
  clearPending();
  exhausted?.();
};

/**
 * Emits an action an instance may refuse — joining, starting or rejoining a
 * conversation — and replays it on each reassignment.
 *
 * The attempt count lives here rather than in the caller because it has to
 * survive the socket reconnect that moves the user between instances, and
 * because instances share no view of how far a user has already travelled.
 */
export const emitPlaced = (emit: PlacedEmit, exhausted: () => void): void => {
  // A placement already in flight owns a live 30s timer. Overwriting timeoutId
  // without clearing it would orphan that timer, and when it fired it would run
  // giveUp against this newer placement: a failure message and cleared state for
  // a hop that was still valid, while the server may already have seated the
  // user. Callers guard against reaching here twice; this keeps the leak closed
  // if one ever misses.
  clearPending();

  pendingEmit = emit;
  onExhausted = exhausted;
  attempts = 0;
  isPlacing.value = true;
  timeoutId = setTimeout(giveUp, placementTimeoutMs);
  emit(attempts);
};

/**
 * Clears the pending action once it has been accepted, so an unrelated later
 * reassignment cannot replay a stale one.
 */
export const settlePlacement = (): void => {
  clearPending();
};

/**
 * Stops waiting at the user's request. Routed through the caller's exhausted
 * handler rather than just hiding the overlay: the pages track their own
 * pending flags (a disabled Join button, for one), and clearing only this
 * module's state would leave those stuck.
 */
export const cancelPlacement = (): void => {
  if (!pendingEmit) {
    return;
  }

  console.info('[placement] Cancelled by the user.');
  giveUp();
};

/**
 * Handles a 'reassign' from the server: drop this instance, take the next one
 * the load balancer offers, and replay the action there. Emits issued while the
 * socket is down are queued by SocketInstance and flush once the new connection
 * reports itself connected.
 */
export const handleReassign = (): void => {
  if (!pendingEmit) {
    // The server refused to seat this user and nothing here knows what to
    // replay, so the hop silently does not happen and the user stays put on an
    // instance that just turned them away. Loud on purpose: the server-side log
    // shows a refusal either way, making this indistinguishable from a working
    // hop unless the client says so.
    console.warn(
      '[placement] Received "reassign" with no pending action to replay — ' +
        'the user will stay on this instance.',
    );
    return;
  }

  console.info(`[placement] Reassigned; retrying on another instance (attempt ${attempts + 1}).`);
  attempts += 1;
  if (attempts > runawayHopCap) {
    giveUp();
    return;
  }

  // Queued explicitly rather than emitted straight after reconnect(): that path
  // also queues (the socket is down by then) but leaves no way to invalidate it,
  // so a cancel during the gap could not stop it. Held to the epoch captured
  // here, the replay is dropped if the placement ended while it waited.
  const emit = pendingEmit;
  const epoch = placementEpoch;
  const attempt = attempts;
  getIoInstance().reconnect();
  getIoInstance().callIfConnected(() => {
    if (epoch !== placementEpoch) {
      console.info('[placement] Dropping a replay for a placement that already ended.');
      return;
    }
    emit(attempt);
  });
};
